<script setup lang="ts">
/**
 * Avatar — circular initials/image representation of a user.
 *
 * Used in subscriber lists, admin user references, partner directory.
 * The prototype generates a random pastel-on-dark color per user;
 * we reproduce that with a deterministic hash of the name so the
 * color is stable across renders.
 *
 * Falls back to initials (first letter of each word, max 2) when no
 * image src is provided.
 */
import { computed } from 'vue'

interface Props {
  name: string
  src?: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
}
const props = withDefaults(defineProps<Props>(), { size: 'sm' })

// Deterministic palette — same name always renders same color.
// Palette pulled from the prototype's seven sample avatars:
// FEE2E2/991B1B, FEF3C7/92400E, DBEAFE/1E40AF, E0E7FF/3730A3,
// FCE7F3/9D174D, D1FAE5/065F46, EDE9FE/5B21B6.
const PALETTE: Array<{ bg: string; fg: string }> = [
  { bg: '#FEE2E2', fg: '#991B1B' },
  { bg: '#FEF3C7', fg: '#92400E' },
  { bg: '#DBEAFE', fg: '#1E40AF' },
  { bg: '#E0E7FF', fg: '#3730A3' },
  { bg: '#FCE7F3', fg: '#9D174D' },
  { bg: '#D1FAE5', fg: '#065F46' },
  { bg: '#EDE9FE', fg: '#5B21B6' },
]

const colors = computed(() => {
  let hash = 0
  for (let i = 0; i < props.name.length; i++) {
    hash = (hash * 31 + props.name.charCodeAt(i)) | 0
  }
  // PALETTE has at least one entry (it's a constant 7-element array),
  // but TS noUncheckedIndexedAccess returns T | undefined. Index is
  // bounded by modulo so the lookup is always defined; the ?? fallback
  // is for the type checker, not real runtime.
  return PALETTE[Math.abs(hash) % PALETTE.length] ?? PALETTE[0]!
})

const initials = computed(() => {
  return props.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join('')
})

const sizeClass = computed(
  () =>
    ({
      xs: 'w-5 h-5 text-[9px]',
      sm: 'w-7 h-7 text-[11px]',
      md: 'w-9 h-9 text-sm',
      lg: 'w-12 h-12 text-base',
    })[props.size],
)
</script>

<template>
  <div
    :class="[
      'shrink-0 rounded-full flex items-center justify-center font-semibold uppercase',
      sizeClass,
    ]"
    :style="
      src
        ? undefined
        : { backgroundColor: colors.bg, color: colors.fg }
    "
  >
    <img
      v-if="src"
      :src="src"
      :alt="name"
      class="w-full h-full rounded-full object-cover"
    />
    <span v-else>{{ initials }}</span>
  </div>
</template>
